import React, { useEffect, useMemo, useState } from 'react';
import {
  X, MapPin, Clock, Briefcase, Navigation, CalendarDays, Sun,
  Banknote, Layers, ArrowUpDown, ShieldAlert, RotateCcw,
} from 'lucide-react';
import LocationInput from './LocationInput';
import WorkdaysPicker from './WorkdaysPicker';
import { fetchJobOptions, fetchFilterCounts } from '../services/api';
import { useApp } from '../context/AppContext';
import { overlayDismiss } from '../utils/overlayDismiss';
import { countActiveFilters, filtersToParams, EMPTY_FILTERS } from '../hooks/useJobFilters';
import {
  JOB_TYPE_LABELS, BRANCHE_LABELS, DAYTIME_LABELS, labelOf,
} from '../constants/jobOptions';
import './FilterDrawer.css';

// Slugs kommen aus GET /jobs/options, die Labels stehen im Frontend.
// Die Keys der Label-Maps sind der Fallback, wenn /options nicht lädt.
const FALLBACK_OPTIONS = {
  job_types: Object.keys(JOB_TYPE_LABELS),
  branchen: Object.keys(BRANCHE_LABELS),
  daytimes: Object.keys(DAYTIME_LABELS),
};

const SORTS = [
  { value: 'entfernung', label: 'Entfernung' },
  { value: 'neueste', label: 'Neueste' },
  { value: 'verguetung', label: 'Vergütung' },
];

const RADIUS_STEPS = [5, 10, 20, 30, 50, 75, 100];

export default function FilterDrawer({
  onClose,
  onApply,
  values = EMPTY_FILTERS,
  defaults = EMPTY_FILTERS,
  isMinor = null,
}) {
  const { user } = useApp();
  const [draft, setDraft] = useState({ ...EMPTY_FILTERS, ...values });
  const [counts, setCounts] = useState({});
  const [total, setTotal] = useState(null);

  const set = (patch) => setDraft(prev => ({ ...prev, ...patch }));

  // Trefferzahlen leben mit: jede Filteränderung fragt neu (entprellt).
  // Der Branchen-Filter selbst wird serverseitig ignoriert, sonst stünde
  // hinter jeder anderen Branche eine 0.
  const countParams = useMemo(() => filtersToParams(draft, user), [draft, user]);
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchFilterCounts(countParams)
        .then(({ data }) => {
          if (cancelled) return;
          setCounts(Object.fromEntries((data?.branchen || []).map(b => [b.branche, b.count])));
          setTotal(data?.total ?? null);
        })
        .catch(() => { if (!cancelled) setTotal(null); });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [countParams]);

  const activeCount = countActiveFilters(draft, defaults);

  // Bei gewählten Branchen zählt nur deren Summe, sonst der Gesamtwert
  const matchCount = draft.branchen.length
    ? draft.branchen.reduce((sum, b) => sum + (counts[b] || 0), 0)
    : total;

  const handleApply = () => {
    onApply && onApply(draft);
    onClose();
  };

  return (
    <div className="filter-overlay" {...overlayDismiss(onClose)}>
      <div className="filter-drawer animate-slideInUp">
        <div className="filter-drawer__handle" />

        <div className="filter-drawer__header">
          <h2>
            Filter
            {activeCount > 0 && <span className="filter-count-badge">{activeCount}</span>}
          </h2>
          <div className="filter-header-actions">
            <button
              className="filter-reset-btn"
              onClick={() => setDraft(defaults)}
              disabled={activeCount === 0}
            >
              <RotateCcw size={14} /> Alle zurücksetzen
            </button>
            <button className="filter-close-btn" onClick={onClose} aria-label="Schließen">
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="filter-drawer__body">
          {/* Altersfilter — nur für Minderjährige sichtbar */}
          {isMinor && (
            <div className="filter-section">
              <label className="filter-switch filter-switch--highlight">
                <input
                  type="checkbox"
                  checked={draft.onlyMyAge}
                  onChange={e => set({ onlyMyAge: e.target.checked })}
                />
                <span className="filter-switch__text">
                  <span className="filter-switch__title">
                    <ShieldAlert size={15} /> Nur Jobs ab meinem Alter
                  </span>
                  <small>Der Jugendarbeitsschutz begrenzt, welche Jobs du unter 18 annehmen darfst.</small>
                </span>
              </label>
            </div>
          )}

          <div className="filter-section">
            <div className="filter-section__label"><ArrowUpDown size={15} /> Sortierung</div>
            <div className="filter-chips">
              {SORTS.map(s => (
                <button
                  key={s.value}
                  className={`filter-chip${draft.sort === s.value ? ' filter-chip--active' : ''}`}
                  onClick={() => set({ sort: s.value })}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {draft.sort === 'entfernung' && draft.locationId == null && (
              <p className="filter-hint">Für die Sortierung nach Entfernung einen Ort aus der Vorschlagsliste wählen.</p>
            )}
          </div>

          <div className="filter-section">
            <div className="filter-section__label"><Navigation size={15} /> Ort</div>
            <LocationInput
              value={draft.location}
              onChange={(text, loc) => set({ location: text, locationId: loc?.id ?? null })}
              inputClassName="filter-location-input"
              placeholder="z.B. Kaiserslautern"
            />
            <div className="filter-radius">
              <div className="filter-radius__head">
                <span><MapPin size={14} /> Umkreis</span>
                <strong>{draft.radius} km</strong>
              </div>
              <input
                type="range"
                min={RADIUS_STEPS[0]}
                max={RADIUS_STEPS[RADIUS_STEPS.length - 1]}
                step={5}
                value={draft.radius}
                onChange={e => set({ radius: Number(e.target.value) })}
                className="filter-radius__slider"
                aria-label="Umkreis in Kilometern"
              />
              <div className="filter-radius__ticks">
                {RADIUS_STEPS.map(km => (
                  <button key={km} className="filter-radius__tick" onClick={() => set({ radius: km })}>
                    {km}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <FilterFields draft={draft} set={set} counts={counts} />
        </div>

        <div className="filter-drawer__footer">
          <button className="filter-apply-btn" onClick={handleApply}>
            {matchCount != null
              ? `${matchCount} Job${matchCount === 1 ? '' : 's'} anzeigen`
              : 'Anwenden'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Die Auswahl-Sektionen ohne Ort, Sortierung und Altersfilter. Geteilt mit dem
// Wünsche-Schritt im Setup (SetupPage), damit beide exakt dieselben Optionen
// zeigen. counts weglassen = keine Trefferzahlen an den Branchen.
export function FilterFields({ draft, set, counts }) {
  const [options, setOptions] = useState(FALLBACK_OPTIONS);

  useEffect(() => {
    fetchJobOptions()
      .then(({ data }) => setOptions({ ...FALLBACK_OPTIONS, ...data }))
      .catch(() => {});
  }, []);

  const toggle = (key, value) => set({
    [key]: draft[key].includes(value) ? draft[key].filter(v => v !== value) : [...draft[key], value],
  });

  const numberField = (key, props) => (
    <input
      type="number"
      className="filter-number"
      value={draft[key]}
      onChange={e => set({ [key]: e.target.value })}
      {...props}
    />
  );

  const switchRow = (key, label) => (
    <label className="filter-switch">
      <input
        type="checkbox"
        checked={draft[key]}
        onChange={e => set({ [key]: e.target.checked })}
      />
      <span className="filter-switch__text">{label}</span>
    </label>
  );

  return (
    <>
      <div className="filter-section">
        <div className="filter-section__label"><Briefcase size={15} /> Jobart</div>
        <div className="filter-chips">
          {options.job_types.map(slug => (
            <button
              key={slug}
              className={`filter-chip${draft.jobTypes.includes(slug) ? ' filter-chip--active' : ''}`}
              onClick={() => toggle('jobTypes', slug)}
            >
              {labelOf(JOB_TYPE_LABELS, slug)}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-section__label"><Layers size={15} /> Branche</div>
        <div className="filter-chips">
          {options.branchen.map(slug => (
            <button
              key={slug}
              className={`filter-chip${draft.branchen.includes(slug) ? ' filter-chip--active' : ''}`}
              onClick={() => toggle('branchen', slug)}
            >
              {labelOf(BRANCHE_LABELS, slug)}
              {counts && <span className="filter-chip__count">{counts[slug] ?? '–'}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-section__label"><CalendarDays size={15} /> Arbeitstage</div>
        <WorkdaysPicker value={draft.weekdays} onChange={weekdays => set({ weekdays })} />
      </div>

      <div className="filter-section">
        <div className="filter-section__label"><Sun size={15} /> Tageszeit</div>
        <div className="filter-chips">
          {options.daytimes.map(slug => (
            <button
              key={slug}
              className={`filter-chip${draft.daytimes.includes(slug) ? ' filter-chip--active' : ''}`}
              onClick={() => toggle('daytimes', slug)}
            >
              {labelOf(DAYTIME_LABELS, slug)}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-section__label"><Clock size={15} /> Umfang</div>
        <div className="filter-range">
          <span className="filter-range__label">Tage / Woche</span>
          {numberField('daysMin', { min: 1, max: 7, placeholder: 'von' })}
          <span className="filter-range__dash">–</span>
          {numberField('daysMax', { min: 1, max: 7, placeholder: 'bis' })}
        </div>
        <div className="filter-range">
          <span className="filter-range__label">Stunden / Woche</span>
          {numberField('hoursMin', { min: 0, max: 60, placeholder: 'von' })}
          <span className="filter-range__dash">–</span>
          {numberField('hoursMax', { min: 0, max: 60, placeholder: 'bis' })}
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-section__label"><Banknote size={15} /> Mindest-Stundenlohn</div>
        <div className="filter-range">
          {numberField('payMin', { min: 0, step: 0.5, placeholder: 'z.B. 13' })}
          <span className="filter-range__unit">€ pro Stunde</span>
        </div>
      </div>

      <div className="filter-section">
        <div className="filter-section__label"><CalendarDays size={15} /> Verfügbar ab</div>
        <input
          type="date"
          className="filter-date"
          value={draft.startDate}
          onChange={e => set({ startDate: e.target.value })}
        />
      </div>

      <div className="filter-section">
        <div className="filter-section__label">Voraussetzungen</div>
        {switchRow('noExperience', 'Keine Vorkenntnisse nötig')}
        {switchRow('noLicense', 'Kein Führerschein nötig')}
        {switchRow('remote', 'Homeoffice möglich')}
      </div>
    </>
  );
}
